// Queue — the runner fleet and the job table behind every run. Reads
// GET /api/sphinx/runners + /jobs (polled every 3s while anything is live);
// operators bump a job's priority or cancel it from here.
import { useState } from "react";
import { ChevronDown, ChevronUp, Loader2, XCircle } from "lucide-react";
import { toast } from "@/lib/toast";
import { Badge } from "@sphinx/design-system/badge";
import { Button } from "@sphinx/design-system/button";
import { cn } from "@/lib/utils";
import {
  cancelJob,
  setJobPriority,
  useQueueOverview,
  type SphinxJob,
  type SphinxRunner,
} from "@/data/queue";
import { targetDisplayName } from "@/data/targets";
import { buildEvalsPath, navigateApp } from "@/lib/app-navigation";
import { isLiveJobState, type JobState } from "@/shared/sphinx";
import {
  EVAL_FAILED_BADGE_STRONG_CLASS,
  EVAL_PASSED_BADGE_STRONG_CLASS,
} from "@/components/evals/constants";

const PRIORITY_MIN = -100;
const PRIORITY_MAX = 100;

const STATE_CLASS: Record<JobState, string> = {
  queued: "bg-muted text-muted-foreground",
  assigned: "bg-warning/20 text-foreground",
  running: "bg-warning/20 text-foreground",
  succeeded: EVAL_PASSED_BADGE_STRONG_CLASS,
  failed: EVAL_FAILED_BADGE_STRONG_CLASS,
  canceled: "bg-muted text-muted-foreground line-through",
  orphaned: EVAL_FAILED_BADGE_STRONG_CLASS,
};

const TH = "px-3 py-2";
const TD = "px-3 py-2 align-top";

function fmtTime(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function ago(value: string | null | undefined): string {
  if (!value) return "never";
  const ms = Date.now() - new Date(value).getTime();
  if (!Number.isFinite(ms) || ms < 0) return fmtTime(value);
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function RunnersTable({ runners }: { runners: SphinxRunner[] }) {
  if (runners.length === 0) {
    return (
      <p className="rounded-lg border px-4 py-6 text-sm text-muted-foreground" data-testid="runners-empty">
        No runners have registered. A Mac worker appears here after its first
        WebSocket <code>register</code>.
      </p>
    );
  }
  return (
    <div className="overflow-hidden rounded-lg border" data-testid="runners-list">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className={TH}>Runner</th>
            <th className={TH}>Capabilities</th>
            <th className={cn(TH, "w-36")}>Slots</th>
            <th className={cn(TH, "w-24")}>In flight</th>
            <th className={cn(TH, "w-32")}>Last seen</th>
          </tr>
        </thead>
        <tbody>
          {runners.map((r) => (
            <tr key={r.id} data-testid={`runner-row-${r.id}`} className="border-t">
              <td className={TD}>
                <div className="flex items-center gap-2">
                  <span
                    aria-label={r.connected ? "connected" : "disconnected"}
                    data-testid={`runner-dot-${r.id}`}
                    data-connected={r.connected ? "true" : "false"}
                    className={cn(
                      "inline-block size-2 rounded-full",
                      r.connected ? "bg-success" : "bg-muted-foreground/40"
                    )}
                  />
                  <span className="font-medium text-foreground">{r.name}</span>
                </div>
                {r.id !== r.name ? (
                  <div className="text-xs text-muted-foreground">{r.id}</div>
                ) : null}
              </td>
              <td className={TD}>
                <div className="flex flex-wrap gap-1">
                  {(r.capabilities ?? []).length === 0 ? (
                    <span className="text-xs text-muted-foreground">none</span>
                  ) : (
                    r.capabilities.map((cap) => (
                      <Badge key={cap} variant="outline" className="font-mono text-[11px]">
                        {cap}
                      </Badge>
                    ))
                  )}
                </div>
              </td>
              <td className={cn(TD, "font-mono text-xs")}>
                {Object.entries(r.slots ?? {})
                  .map(([k, v]) => `${k}:${v}`)
                  .join(" ") || "—"}
              </td>
              <td className={TD}>{r.inFlight}</td>
              <td className={cn(TD, "text-muted-foreground")} title={r.lastSeenAt ?? undefined}>
                {ago(r.lastSeenAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function JobsTable({
  jobs,
  busyId,
  onBump,
  onCancel,
}: {
  jobs: SphinxJob[];
  busyId: string | null;
  onBump: (job: SphinxJob, delta: number) => void;
  onCancel: (job: SphinxJob) => void;
}) {
  if (jobs.length === 0) {
    return (
      <p className="rounded-lg border px-4 py-6 text-sm text-muted-foreground" data-testid="jobs-empty">
        No jobs. Start a run from Evaluate and it shows up here.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto rounded-lg border" data-testid="jobs-list">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className={TH}>Created</th>
            <th className={TH}>Suite / case</th>
            <th className={TH}>Client</th>
            <th className={TH}>Model</th>
            <th className={TH}>Env</th>
            <th className={TH}>Org</th>
            <th className={TH}>Class</th>
            <th className={TH}>State</th>
            <th className={TH}>Priority</th>
            <th className={TH}>Runner</th>
            <th className={TH}>Started</th>
            <th className={TH}>Finished</th>
            <th className={cn(TH, "w-10")} />
          </tr>
        </thead>
        <tbody>
          {jobs.map((job) => {
            const live = isLiveJobState(job.state);
            const busy = busyId === job.id;
            const { target, model, yarnEnv, yarnOrg, caseTitle } = job.specSummary;
            const runHref =
              job.suiteId && job.runId
                ? buildEvalsPath({ type: "run-detail", suiteId: job.suiteId, runId: job.runId })
                : null;
            return (
              <tr
                key={job.id}
                data-testid={`job-row-${job.id}`}
                data-state={job.state}
                className={cn("border-t", !live && "text-muted-foreground")}
              >
                <td className={cn(TD, "whitespace-nowrap")} title={job.createdAt}>
                  {fmtTime(job.createdAt)}
                </td>
                <td className={TD}>
                  {runHref ? (
                    <a
                      href={runHref}
                      className="font-medium text-foreground hover:underline"
                      data-testid={`job-run-link-${job.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigateApp(runHref);
                      }}
                    >
                      {job.suiteName ?? "Suite"}
                    </a>
                  ) : (
                    <span className="font-medium">{job.suiteName ?? "—"}</span>
                  )}
                  <div className="max-w-[28ch] truncate text-xs text-muted-foreground" title={caseTitle ?? undefined}>
                    {caseTitle ?? job.iterationId}
                  </div>
                </td>
                <td className={cn(TD, "whitespace-nowrap")}>{target ? targetDisplayName(target) : "—"}</td>
                <td className={cn(TD, "whitespace-nowrap font-mono text-xs")}>{model ?? "—"}</td>
                <td className={TD}>{yarnEnv ?? "—"}</td>
                <td className={TD} data-testid={`job-org-${job.id}`}>{yarnOrg ?? "—"}</td>
                <td className={cn(TD, "font-mono text-xs")}>{job.class}</td>
                <td className={TD}>
                  <Badge
                    variant="outline"
                    className={cn("border-transparent", STATE_CLASS[job.state])}
                    data-testid={`job-state-${job.id}`}
                  >
                    {job.state}
                  </Badge>
                  {job.cancelRequested && live ? (
                    <div className="text-[11px] text-muted-foreground">cancel requested</div>
                  ) : null}
                  {job.state === "queued" && job.blockedBy ? (
                    <div
                      className="max-w-[32ch] text-[11px] text-muted-foreground"
                      data-testid={`job-blocked-${job.id}`}
                      title={job.blockedBy}
                    >
                      {job.blockedBy}
                    </div>
                  ) : null}
                  {job.requeueCount > 0 ? (
                    <div className="text-[11px] text-muted-foreground">requeued ×{job.requeueCount}</div>
                  ) : null}
                </td>
                <td className={cn(TD, "whitespace-nowrap")}>
                  <div className="flex items-center gap-1">
                    <span className="w-8 text-right font-mono tabular-nums" data-testid={`job-priority-${job.id}`}>
                      {job.priority}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-6"
                      aria-label="Raise priority"
                      disabled={busy || !live || job.priority >= PRIORITY_MAX}
                      onClick={() => onBump(job, 1)}
                      data-testid={`job-bump-up-${job.id}`}
                    >
                      <ChevronUp className="size-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-6"
                      aria-label="Lower priority"
                      disabled={busy || !live || job.priority <= PRIORITY_MIN}
                      onClick={() => onBump(job, -1)}
                      data-testid={`job-bump-down-${job.id}`}
                    >
                      <ChevronDown className="size-3.5" />
                    </Button>
                  </div>
                </td>
                <td className={cn(TD, "whitespace-nowrap")}>
                  {job.assignedRunnerName ?? job.assignedRunner ?? "—"}
                </td>
                <td className={cn(TD, "whitespace-nowrap")} title={job.startedAt ?? undefined}>
                  {fmtTime(job.startedAt)}
                </td>
                <td className={cn(TD, "whitespace-nowrap")} title={job.finishedAt ?? undefined}>
                  {fmtTime(job.finishedAt)}
                </td>
                <td className={TD}>
                  {live ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-6 text-muted-foreground hover:text-destructive"
                      aria-label="Cancel job"
                      disabled={busy || job.cancelRequested}
                      onClick={() => onCancel(job)}
                      data-testid={`job-cancel-${job.id}`}
                    >
                      {busy ? <Loader2 className="size-3.5 animate-spin" /> : <XCircle className="size-3.5" />}
                    </Button>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function QueuePage() {
  const overview = useQueueOverview();
  const [busyId, setBusyId] = useState<string | null>(null);

  const run = async (jobId: string, fn: () => Promise<unknown>, failure: string) => {
    setBusyId(jobId);
    try {
      await fn();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : failure);
    } finally {
      setBusyId(null);
    }
  };

  const handleBump = (job: SphinxJob, delta: number) => {
    const next = Math.max(PRIORITY_MIN, Math.min(PRIORITY_MAX, job.priority + delta));
    if (next === job.priority) return;
    void run(job.id, () => setJobPriority(job.id, next), "Failed to change priority");
  };

  const handleCancel = (job: SphinxJob) =>
    void run(job.id, () => cancelJob(job.id), "Failed to cancel job");

  if (overview === undefined) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const runners = overview?.runners ?? [];
  const jobs = overview?.jobs ?? [];
  const liveCount = jobs.filter((j) => isLiveJobState(j.state)).length;
  const connectedCount = runners.filter((r) => r.connected).length;

  return (
    <div className="flex h-full flex-col gap-6 overflow-y-auto p-6">
      <div>
        <h1 className="text-2xl font-bold">Queue</h1>
        <p className="text-sm text-muted-foreground" data-testid="queue-summary">
          {connectedCount} of {runners.length} runner{runners.length === 1 ? "" : "s"} connected ·{" "}
          {liveCount} live job{liveCount === 1 ? "" : "s"}. Server-class jobs run in the
          server's own worker pool; mac-class jobs go to a connected runner with the
          required capabilities.
        </p>
      </div>

      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold">Runners</h2>
        <RunnersTable runners={runners} />
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold">Jobs</h2>
        <JobsTable jobs={jobs} busyId={busyId} onBump={handleBump} onCancel={handleCancel} />
      </section>
    </div>
  );
}
